import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";

const TYPES = [
    { value: "text", label: "Text" },
    { value: "number", label: "Number" },
    { value: "select", label: "Select" },
    { value: "checkbox", label: "Checkbox" },
];

const BLANK_FIELD = () => ({
    name: "",
    label: "",
    type: "text",
    required: false,
    min: "",
    max: "",
    options: "",
});

/**
 * CalculatorFieldsEditor — dynamic add/remove row editor for the field
 * list a formula-driven `deal.calculator` config needs. Each row's `name`
 * is the variable the formula (evaluated in CalculatorModal) references.
 */
export default function CalculatorFieldsEditor({ fields, onChange }) {
    const rows = fields || [];

    const updateRow = (idx, patch) => {
        onChange(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
    };
    const removeRow = (idx) => {
        onChange(rows.filter((_, i) => i !== idx));
    };
    const addRow = () => {
        onChange([...rows, BLANK_FIELD()]);
    };

    return (
        <div className="space-y-3" data-testid="calc-fields-editor">
            {rows.length === 0 && (
                <p className="text-[11px] text-navy-500">
                    No fields yet — add one for each input the formula should use.
                </p>
            )}
            {rows.map((row, idx) => (
                <div
                    key={idx}
                    data-testid={`calc-field-row-${idx}`}
                    className="rounded-xl border border-navy-100 bg-white p-4 space-y-3"
                >
                    <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                            <Label className="text-navy-700 text-[10px] uppercase tracking-widest">
                                Name (formula variable)
                            </Label>
                            <Input
                                value={row.name}
                                onChange={(e) => updateRow(idx, { name: e.target.value })}
                                placeholder="cover_amount"
                                data-testid={`calc-field-row-${idx}-name`}
                                className="mt-1.5 h-10 bg-white border-navy-100 font-mono text-sm"
                            />
                        </div>
                        <div>
                            <Label className="text-navy-700 text-[10px] uppercase tracking-widest">
                                Label (shown to member)
                            </Label>
                            <Input
                                value={row.label}
                                onChange={(e) => updateRow(idx, { label: e.target.value })}
                                placeholder="Cover amount"
                                data-testid={`calc-field-row-${idx}-label`}
                                className="mt-1.5 h-10 bg-white border-navy-100 text-sm"
                            />
                        </div>
                    </div>

                    <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
                        <div>
                            <Label className="text-navy-700 text-[10px] uppercase tracking-widest">
                                Type
                            </Label>
                            <Select
                                value={row.type}
                                onValueChange={(v) => updateRow(idx, { type: v })}
                            >
                                <SelectTrigger
                                    data-testid={`calc-field-row-${idx}-type`}
                                    className="mt-1.5 h-10 bg-white border-navy-100"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-white">
                                    {TYPES.map((t) => (
                                        <SelectItem key={t.value} value={t.value}>
                                            {t.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {row.type === "number" && (
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <Label className="text-navy-700 text-[10px] uppercase tracking-widest">
                                        Min
                                    </Label>
                                    <Input
                                        type="number"
                                        value={row.min}
                                        onChange={(e) => updateRow(idx, { min: e.target.value })}
                                        data-testid={`calc-field-row-${idx}-min`}
                                        className="mt-1.5 h-10 bg-white border-navy-100 text-sm"
                                    />
                                </div>
                                <div>
                                    <Label className="text-navy-700 text-[10px] uppercase tracking-widest">
                                        Max
                                    </Label>
                                    <Input
                                        type="number"
                                        value={row.max}
                                        onChange={(e) => updateRow(idx, { max: e.target.value })}
                                        data-testid={`calc-field-row-${idx}-max`}
                                        className="mt-1.5 h-10 bg-white border-navy-100 text-sm"
                                    />
                                </div>
                            </div>
                        )}

                        {row.type === "select" && (
                            <div>
                                <Label className="text-navy-700 text-[10px] uppercase tracking-widest">
                                    Options (comma-separated)
                                </Label>
                                <Input
                                    value={row.options}
                                    onChange={(e) => updateRow(idx, { options: e.target.value })}
                                    placeholder="Basic, Standard, Premium"
                                    data-testid={`calc-field-row-${idx}-options`}
                                    className="mt-1.5 h-10 bg-white border-navy-100 text-sm"
                                />
                            </div>
                        )}

                        <div className="flex items-center gap-3 sm:justify-self-end">
                            <label
                                htmlFor={`calc-field-row-${idx}-required`}
                                className="flex items-center gap-2 cursor-pointer whitespace-nowrap"
                            >
                                <Checkbox
                                    id={`calc-field-row-${idx}-required`}
                                    data-testid={`calc-field-row-${idx}-required`}
                                    checked={row.required}
                                    onCheckedChange={(v) =>
                                        updateRow(idx, { required: Boolean(v) })
                                    }
                                />
                                <span className="text-xs text-navy-700">Required</span>
                            </label>
                            <button
                                type="button"
                                onClick={() => removeRow(idx)}
                                data-testid={`calc-field-row-${idx}-remove`}
                                className="text-navy-300 hover:text-red-500 p-1.5"
                                aria-label="Remove field"
                            >
                                <Trash2 size={16} />
                            </button>
                        </div>
                    </div>
                </div>
            ))}

            <Button
                type="button"
                variant="outline"
                onClick={addRow}
                data-testid="calc-field-add"
                className="rounded-full border-navy-200 text-navy-700 hover:bg-navy-50 h-10"
            >
                <Plus size={14} className="mr-2" /> Add field
            </Button>
        </div>
    );
}
